import { useState, useMemo } from 'react'
import { Link, useLocation } from 'react-router'
import { Modal } from '@carouselgg/nexus/modal'
import { Button } from '@carouselgg/nexus/button'
import { FormField } from '@carouselgg/nexus/form-field'
import { DateTimePicker } from '@carouselgg/nexus/date-time-picker'
import { Checkbox } from '@carouselgg/nexus/checkbox'
import { Text } from '@carouselgg/nexus/text'
import { Callout } from '@carouselgg/nexus/callout'
import { useAuthStore } from '@carouselgg/auth/store'
import styles from './ComplianceModal.module.css'

// Pages where the modal should not block — let the user read terms/privacy
const EXEMPT_PATHS = ['/terms', '/privacy', '/login', '/signup']

export function ComplianceModal() {
  const location = useLocation()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const isLoading = useAuthStore((s) => s.isLoading)
  const termsAcceptedAt = useAuthStore((s) => s.termsAcceptedAt)
  const dateOfBirth = useAuthStore((s) => s.dateOfBirth)
  const acceptTerms = useAuthStore((s) => s.acceptTerms)
  const submitDateOfBirth = useAuthStore((s) => s.submitDateOfBirth)

  const [termsChecked, setTermsChecked] = useState(false)
  const [dob, setDob] = useState('')
  const [parentalConsent, setParentalConsent] = useState(false)
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const isExemptPage = EXEMPT_PATHS.includes(location.pathname)
  const needsTerms = isAuthenticated && !isLoading && !termsAcceptedAt
  const needsDob = isAuthenticated && !isLoading && !dateOfBirth
  const isOpen = !isExemptPage && (needsTerms || needsDob)

  const computedAge = useMemo(() => {
    if (!dob) return null
    const dobDate = new Date(dob)
    const now = new Date()
    let age = now.getFullYear() - dobDate.getFullYear()
    if (now.getMonth() < dobDate.getMonth() || (now.getMonth() === dobDate.getMonth() && now.getDate() < dobDate.getDate())) age--
    return age
  }, [dob])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')

    if (needsTerms && !termsChecked) {
      setError('You must accept the Terms of Service and Privacy Policy')
      return
    }
    if (needsDob) {
      if (!dob) {
        setError('Date of birth is required')
        return
      }
      if (computedAge !== null && computedAge < 13) {
        setError('You must be at least 13 years old to use Carousel')
        return
      }
      if (computedAge !== null && computedAge < 18 && !parentalConsent) {
        setError('Parental or guardian consent is required for users under 18')
        return
      }
    }

    setSubmitting(true)
    try {
      if (needsTerms) await acceptTerms()
      if (needsDob) await submitDateOfBirth(dob.split('T')[0], parentalConsent)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong')
    } finally {
      setSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <Modal
      open
      onClose={() => undefined}
      closeOnBackdrop={false}
      closeOnEscape={false}
      showCloseButton={false}
      title="We need a few things before you continue"
      size="sm"
    >
      <form className={styles.form} onSubmit={handleSubmit}>
        <Text size="sm" className={styles.description}>
          To keep Carousel safe for everyone, we need you to review our updated terms and provide your date of birth.
        </Text>

        {needsTerms && (
          <Checkbox
            label={<>I agree to the <Link to="/terms" target="_blank">Terms of Service</Link> and <Link to="/privacy" target="_blank">Privacy Policy</Link></>}
            checked={termsChecked}
            onChange={(e) => setTermsChecked(e.target.checked)}
          />
        )}

        {needsDob && (
          <>
            <FormField label="Date of Birth" htmlFor="compliance-dob">
              <DateTimePicker
                mode="date"
                value={dob}
                onChange={setDob}
                placeholder="Select your date of birth"
                max={new Date().toISOString()}
                displayFormat="MMMM D, YYYY"
              />
            </FormField>

            {computedAge !== null && computedAge >= 13 && computedAge < 18 && (
              <Checkbox
                label="I confirm a parent or legal guardian has consented to my use of this platform"
                checked={parentalConsent}
                onChange={(e) => setParentalConsent(e.target.checked)}
              />
            )}
          </>
        )}

        {error && <Callout variant="error" icon>{error}</Callout>}

        <Button type="submit" variant="primary" size="lg" disabled={submitting}>
          {submitting ? 'Saving...' : 'Continue'}
        </Button>
      </form>
    </Modal>
  )
}
